import { SaleOrderBearingVisibility } from './../../models/sale-order-bearing-visibility';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { SaleOrderBuilder } from '../../models/builders/sale-order-builder';
import { SaleOrder } from '../../models/sale-order';
import { SaleOrderLine, TYPE_COMPLEMENT, TYPE_OTHER, TYPE_PRODUCT } from '../../models/sale-order-line';
import { SaleOrderLineProduct } from '../../models/sale-order-line-product';
import { SaleOrderLineComplement } from '../../models/sale-order-line-complement';
import { SaleOrderLineOther } from '../../models/sale-order-line-other';
import { mapCollection } from '../../utils/collection.utils';
import { SaleOrdersRequestInterface } from '../../models/request/sale-orders-request.interface';
import { SaleOrderDepartureBuilder } from '../../models/builders/sale-order-departure-builder';
import { SaleOrderDepartureReasonBuilder } from '../../models/builders/sale-order-departure-reason.builder';
import { SaleOrderDeparture } from '../../models/sale-order-departure';
import { SaleOrderDepartureSerialNumber } from '../../models/sale-order-departure-serial-number';
import { SaleOrderPreparation } from '../../models/sale-order-preparation';
import { SaleOrderPreparationBuilder } from '../../models/builders/sale-order-preparation-builder';
import { SaleOrderBearing } from '../../models/sale-order-bearing';
import { SaleOrderBearingBuilder } from '../../models/builders/sale-order-bearing-builder';
import { SaleOrderDepartureSerialNumberBuilder } from '../../models/builders/sale-order-departure-serial-number.builder';
import { SaleOrderControlDocument } from '../../models/sale-order-control-document';
import { SaleOrderControlDocumentBuilder } from '../../models/builders/sale-order-control-document-builder';

const createSaleOrderLineOtherBody = (saleOrderLineOther: SaleOrderLineOther) => (
    {
      concept: saleOrderLineOther.concept,
      amount: saleOrderLineOther.amount
    }
);

const createSaleOrderLineComplementBody = (saleOrderLineComplement: SaleOrderLineComplement) => (
    {
      complementHash: saleOrderLineComplement.complementHash,
      totalAmount: saleOrderLineComplement.totalAmount
    }
);

const createSaleOrderLineProductBody = (saleOrderLineProduct: SaleOrderLineProduct) => (
    {
      productHash: saleOrderLineProduct.productHash,
      discountType: saleOrderLineProduct.discountType,
      discountNumber: saleOrderLineProduct.discountNumber,
      discountAppliedAmount: saleOrderLineProduct.discountAppliedAmount,
      commercialMargin: saleOrderLineProduct.commercialMargin
    }
);

const createSaleOrderLineBody = (saleOrderLine: SaleOrderLine) => (
    {
      hash: saleOrderLine.hash,
      type: saleOrderLine.type,
      saleOrderLineProduct: (saleOrderLine.type === TYPE_PRODUCT) ? createSaleOrderLineProductBody(
          saleOrderLine.saleOrderLineProduct
      ) : null,
      saleOrderLineComplement: (saleOrderLine.type === TYPE_COMPLEMENT) ? createSaleOrderLineComplementBody(
          saleOrderLine.saleOrderLineComplement
      ) : null,
      saleOrderLineOther: (saleOrderLine.type === TYPE_OTHER) ? createSaleOrderLineOtherBody(
          saleOrderLine.saleOrderLineOther
      ) : null
    }
);

const createSaleOrderBody = (saleOrder: SaleOrder) => (
    {
      orderDate: saleOrder.orderDate,
      customerHash: saleOrder.customerHash,
      traderHash: saleOrder.traderHash,
      saleOrderLines: mapCollection((saleOrderLine: SaleOrderLine) => {
        return createSaleOrderLineBody(saleOrderLine);
      }, saleOrder.saleOrderLines),
      observations: saleOrder.observations,
      additionalData: saleOrder.additionalData,
      internalObservations: saleOrder.internalObservations,
      paymentMethodHash: saleOrder.paymentMethodHash,
      paymentMethodDescription: saleOrder.paymentMethodDescription,
      personWhoIsGoingToSign: saleOrder.personWhoIsGoingToSign,
      isDeposit: saleOrder.isDeposit,
      subTotalAmount: saleOrder.subTotalAmount,
      vat: saleOrder.vat,
      totalVat: saleOrder.totalVat,
      totalAmount: saleOrder.totalAmount
    }
);

const createSaleOrderDepartureSerialNumberBody = (saleOrderDepartureSerialNumber: SaleOrderDepartureSerialNumber) => (
    {
      saleOrderLineHash: saleOrderDepartureSerialNumber.saleOrderLineHash,
      serialNumber: saleOrderDepartureSerialNumber.serialNumber
    }
);

const saleOrderDepartureBody = (saleOrderDeparture: SaleOrderDeparture) => (
    {
      departureDate: saleOrderDeparture.departureDate,
      authorizingPersonHash: saleOrderDeparture.authorizingPersonHash,
      requestMadeByHash: saleOrderDeparture.requestMadeByHash,
      saleOrderDepartureReasonHash: saleOrderDeparture.saleOrderDepartureReasonHash,
      observations: saleOrderDeparture.observations,
      duration: saleOrderDeparture.duration,
      saleOrderDepartureSerialNumbers: mapCollection((saleOrderDepartureSerialNumber: SaleOrderDepartureSerialNumber) => {
        return createSaleOrderDepartureSerialNumberBody(saleOrderDepartureSerialNumber);
      }, saleOrderDeparture.saleOrderDepartureSerialNumbers),
    }
);

const saleOrderPreparationBody = (saleOrderPreparation: SaleOrderPreparation) => (
    {
      preparationDate: saleOrderPreparation.preparationDate,
      observations: saleOrderPreparation.observations
    }
);


const createSaleOrderLinesVisbility = (saleOrderBearingVisibility: SaleOrderBearingVisibility) => (
    saleOrderBearingVisibility.saleOrderLineHash
);

const saleOrderBearingBody = (saleOrderBearing: SaleOrderBearing) => (
    {
      bearingDate: saleOrderBearing.bearingDate,
      observations: saleOrderBearing.observations,
      loadingCustomerHash: saleOrderBearing.loadingCustomerHash,
      loadingPlace: saleOrderBearing.loadingPlace,
      loadingCustomerNif: saleOrderBearing.loadingCustomerNif,
      unloadingCustomerHash: saleOrderBearing.unloadingCustomerHash,
      unloadingPlace: saleOrderBearing.unloadingPlace,
      unloadingCustomerNif: saleOrderBearing.unloadingCustomerNif,
      saleOrderLineHashes: mapCollection((saleOrderBearingVisibility: SaleOrderBearingVisibility) => {
        return createSaleOrderLinesVisbility(saleOrderBearingVisibility);
      }, saleOrderBearing.saleOrderBearingVisibilities)
    }
);

const saleOrderControlDocumentBody = (saleOrderControlDocument: SaleOrderControlDocument) => (
    {
      saleOrderHash: saleOrderControlDocument.saleOrderHash,
      date: saleOrderControlDocument.date,
      transportOperator: saleOrderControlDocument.transportOperator,
      carrier: saleOrderControlDocument.carrier,
      trailer: saleOrderControlDocument.trailer,
      enrollment: saleOrderControlDocument.enrollment,
      viewLogoInPdf: saleOrderControlDocument.viewLogoInPdf,
      observation: saleOrderControlDocument.observation,
      packingUnits: saleOrderControlDocument.packingUnits
    }
);

const createEmailFieldBody = (email: string) => (
    {
      email: email
    }
);

const createSignedByCustomerDateFieldBody = (signedDate: number) => (
    {
      signedByCustomerDate: signedDate
    }
);

@Injectable()
export class SaleOrderApiService {
  private apiUrl: string;

  constructor(
      private httpClient: HttpClient
  ) {
    this.apiUrl = `${environment.apiUrl}sale-orders`;
  }

  getSaleOrder(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}`)
        .pipe(
            map(SaleOrderBuilder.fromJson)
        );
  }

  getSaleOrderDeparture(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}/departure`)
        .pipe(
            map(SaleOrderDepartureBuilder.fromJson)
        );
  }

  getSaleOrderPreparation(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}/preparation`)
        .pipe(
            map(SaleOrderPreparationBuilder.fromJson)
        );
  }

  getSaleOrderBearing(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}/bearing`)
        .pipe(
            map(SaleOrderBearingBuilder.fromJson)
        );
  }

  getSaleOrderControlDocument(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}/control-document`)
        .pipe(
            map(SaleOrderControlDocumentBuilder.fromJson)
        );
  }

  getSaleOrderDefaults() {
    return this.httpClient.get(`${this.apiUrl}/defaults`).pipe(map(SaleOrderBuilder.fromJson));
  }

  getSaleOrders() {
    return this.httpClient.get(this.apiUrl).pipe(map(SaleOrderBuilder.fromJsonList));
  }

  getSaleOrderDepartureReasons() {
    return this.httpClient.get(`${this.apiUrl}/departure-reasons`).pipe(map(SaleOrderDepartureReasonBuilder.fromJsonList));
  }

  getSaleOrderDepartureSerialNumbers() {
    return this.httpClient.get(`${this.apiUrl}/departure-serial-numbers`).pipe(map(SaleOrderDepartureSerialNumberBuilder.fromJsonList));
  }

  previewSaleOrder(saleOrderHash: string) {
    return this.httpClient.get(`${this.apiUrl}/${saleOrderHash}/pdf-preview`, { responseType: 'arraybuffer' })
        .map((arrayBuffer: ArrayBuffer) => new Uint8Array(arrayBuffer));
  }

  downloadSaleOrderPdf(saleOrderHash: string) {
    return this.httpClient.get(`${this.apiUrl}/${saleOrderHash}/pdf`, { responseType: 'arraybuffer' });
  }

  downloadInvoicePdf(saleOrderHash: string) {
    return this.httpClient.get(`${this.apiUrl}/${saleOrderHash}/invoice-pdf`, { responseType: 'arraybuffer' });
  }

  downloadDeparturePdf(saleOrderHash: string): any {
    return this.httpClient.get(`${this.apiUrl}/${saleOrderHash}/departure-pdf`, { responseType: 'arraybuffer' });
  }

  downloadPreparationPdf(saleOrderHash: string): any {
    return this.httpClient.get(`${this.apiUrl}/${saleOrderHash}/preparation-pdf`, { responseType: 'arraybuffer' });
  }

  downloadBearingPdf(saleOrderHash: string): any {
    return this.httpClient.get(`${this.apiUrl}/${saleOrderHash}/bearing-pdf`, { responseType: 'arraybuffer' });
  }

  downloadControlDocumentPdf(saleOrderHash: string): any {
    return this.httpClient.get(`${this.apiUrl}/${saleOrderHash}/control-document-pdf`, { responseType: 'arraybuffer' });
  }

  finishSaleOrder(saleOrderHash: string) {
    return this.httpClient.post(`${this.apiUrl}/${saleOrderHash}/finish`, {});
  }

  continueToNextStep(saleOrderHash: string) {
    return this.httpClient.post(`${this.apiUrl}/${saleOrderHash}/next-step`, {});
  }

  sendSaleOrderPdfByEmail(saleOrderHash: string, email: string) {
    return this.httpClient.post(`${this.apiUrl}/${saleOrderHash}/pdf-send`, createEmailFieldBody(email));
  }

  setSignedByCustomer(saleOrderHash: string, pickedMillis: number) {
    return this.httpClient.put(`${this.apiUrl}/${saleOrderHash}/signature`, createSignedByCustomerDateFieldBody(pickedMillis));
  }

  getFilteredSaleOrders(request: SaleOrdersRequestInterface) {
    let params = new HttpParams();
    params = params.append('page', request.page.toString())
        .append('itemsPerPage', request.itemsPerPage.toString())
        .append('sortField', request.sortField)
        .append('reverse', (!!request.reverse).toString());
    if (request.status) {
      params = params.append('status', request.status.toString());
    }
    if (request.customerHash) {
      params = params.append('customerHash', request.customerHash);
    }
    if (request.traderHash) {
      params = params.append('traderHash', request.traderHash);
    }
    if (request.isDeposit) {
      params = params.append('isDeposit', request.isDeposit.toString());
    }

    return this.httpClient.get(`${this.apiUrl}/filter`, { params: params })
        .pipe(
            map(this.mapData)
        );
  }

  saveSaleOrder(saleOrder: SaleOrder) {
    return this.httpClient.post(`${this.apiUrl}`, createSaleOrderBody(saleOrder)).pipe(
        map(SaleOrderBuilder.fromJson)
    );
  }

  updateSaleOrder(saleOrderHash: string, saleOrder: SaleOrder) {
    return this.httpClient.put(`${this.apiUrl}/${saleOrderHash}`, createSaleOrderBody(saleOrder)).pipe(
        map(SaleOrderBuilder.fromJson)
    );
  }

  createSaleOrderDeparture(saleOrderHash: string, saleOrderDeparture: SaleOrderDeparture) {
    return this.httpClient.post(`${this.apiUrl}/${saleOrderHash}/departure`, saleOrderDepartureBody(saleOrderDeparture)).pipe(
        map(SaleOrderDepartureBuilder.fromJson)
    );
  }

  updateSaleOrderDeparture(saleOrderHash: string, saleOrderDeparture: SaleOrderDeparture) {
    return this.httpClient.put(`${this.apiUrl}/${saleOrderHash}/departure/${saleOrderDeparture.hash}`, saleOrderDepartureBody(
        saleOrderDeparture)).pipe(map(SaleOrderDepartureBuilder.fromJson)
    );
  }

  deleteSaleOrderByHash(saleOrderHash: string) {
    return this.httpClient.delete(`${this.apiUrl}/${saleOrderHash}`);
  }

  createSaleOrderPreparation(saleOrderHash: string, saleOrderPreparation: SaleOrderPreparation) {
    return this.httpClient.post(`${this.apiUrl}/${saleOrderHash}/preparation`, saleOrderPreparationBody(saleOrderPreparation)).pipe(
        map(SaleOrderPreparationBuilder.fromJson)
    );
  }

  updateSaleOrderPreparation(saleOrderHash: string, saleOrderPreparation: SaleOrderPreparation) {
    return this.httpClient.put(`${this.apiUrl}/${saleOrderHash}/preparation/${saleOrderPreparation.hash}`, saleOrderPreparationBody(
        saleOrderPreparation)
    );
  }

  createSaleOrderBearing(saleOrderHash: string, saleOrderBearing: SaleOrderBearing) {
    return this.httpClient.post(`${this.apiUrl}/${saleOrderHash}/bearing`, saleOrderBearingBody(saleOrderBearing)).pipe(
        map(SaleOrderBearingBuilder.fromJson)
    );
  }

  updateSaleOrderBearing(saleOrderHash: string, saleOrderBearing: SaleOrderBearing) {
    return this.httpClient.put(
        `${this.apiUrl}/${saleOrderHash}/bearing/${saleOrderBearing.hash}`,
        saleOrderBearingBody(saleOrderBearing)
    );
  }

  createSaleOrderControlDocument(saleOrderControlDocument: SaleOrderControlDocument) {
    return this.httpClient.post(
        `${this.apiUrl}/${saleOrderControlDocument.saleOrderHash}/control-document`,
        saleOrderControlDocumentBody(saleOrderControlDocument)
    );
  }

  updateSaleOrderControlDocument(saleOrderControlDocument: SaleOrderControlDocument) {
    return this.httpClient.put(
        `${this.apiUrl}/${saleOrderControlDocument.saleOrderHash}/control-document/${saleOrderControlDocument.hash}`,
        saleOrderControlDocumentBody(saleOrderControlDocument)
    );
  }

  private mapData = (data) => {
    data.saleOrders = SaleOrderBuilder.fromJsonList(data.saleOrders);
    return data;
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/ubaristi-app-test/workspace/src/app/core/services/api/sale-order-api.service.ts